import { setup, createActor, fromPromise } from "xstate"
import type { SwipeMachineEventType } from "./types"
import router from "@/router"
import { useAnswersStore } from "@/features/swipe/store/answers.store"
import { useQuestionsStore } from "@/features/swipe/store/questions.store"
import { globalActor } from "@/machines/globalMachine"
import { useCookies } from "@vueuse/integrations/useCookies.js"
import { useUserStore } from "@/stores/user"
import { useI18nStore } from "@/stores/i18n"
import { nextTick } from "vue"
import { type GlobalMachineEvent } from "@/machines/types"
import { COOKIES } from "@/config/constants"
import { storeToRefs } from "pinia"
import { useSessionStorage } from "@/helpers/useSessionStorage"
import { useHelpStore } from "@/components/af/Help/help.store"
import { withAssessmentLoadingMinDuration } from "@/features/assessment-loading/assessmentLoadingDuration"

// TODO: remove autoCompleteExtras and submitting state once backend no longer requires native_language and wholesome_conditions
const autoCompleteExtras = fromPromise(async () => {
  const { patchExtra } = useAnswersStore()
  await patchExtra("native_language", true)
  await patchExtra("wholesome_conditions", true)
})

// Loader for Swipe datas
// to redirect to Intro or WelcomeBack
const loadSwipeData = fromPromise(() =>
  withAssessmentLoadingMinDuration("SWIPE", async () => {
    const { fetchSwipe } = useAnswersStore()
    const { fetchAudios } = useQuestionsStore()

    const { showLang } = storeToRefs(useI18nStore())

    const data = await fetchSwipe()

    const assessmentSummary = useUserStore().assessmentSummary.assessments.swipe
    const isFinisedAndNotPassed =
      data?.status === "finish" && assessmentSummary?.not_passed

    // Legacy, the test has been not_passed and considered with status finish in assessment summary (old) but not in workflow and next_assessment (new)
    let assessmentStatus = isFinisedAndNotPassed ? "todo" : data?.status

    // The language can only be changed as long as the test is not started
    showLang.value = assessmentStatus !== "inprogress"

    // The test can only be not_passed as long as it is not started
    if (assessmentStatus === "inprogress") {
      useHelpStore().canAssessmentBePassed.swipe = false // Disabled not passed assessment button
    }

    // status can be "todo", "inprogress" or "finish"
    switch (assessmentStatus) {
      case "todo":
        await fetchAudios()
        return { status: "new" }

      case "inprogress":
        await fetchAudios()
        // If there is less answers than questions, we need to show the questions
        if (
          data.data.tutorial_time?.finished === false ||
          data.data.trial?.finished === false
        ) {
          return { status: "new" }
        } else if (data.data.extra.all_items_completed === false) {
          return { status: "showQuestions" }
        }
        // If native language or wholesome conditions are missing, auto-complete
        // them (post-test screens removed) before loading results
        else if (
          data.data.extra?.native_language == undefined ||
          data.data.extra?.native_language == null ||
          data.data.extra?.wholesome_conditions == undefined ||
          data.data.extra?.wholesome_conditions == null
        ) {
          return { status: "showSubmitting" }
        }
        // If the user has complete every steps but status is inprogress, we need to send the results to backend
        else {
          return { status: "showLoadResults" }
        }

      case "redirect-contact":
        globalActor.send({ type: "GLOBAL.GUEST.CONTACTSUPPORT" })
        return

      case "thank-you":
        router.replace({ name: "thanksATS" })
        return

      case "finish":
        return { status: "showFinal" }

      default:
        // await fetchQuestions()
        await fetchAudios()
        return { status: "new" }
    }
  })
)

export const swipeMachine = setup({
  actors: {
    loadSwipeData,
    autoCompleteExtras,
  },
  types: {} as {
    events: SwipeMachineEventType
  },
  actions: {},
}).createMachine({
  id: "swipeMachine",
  initial: "loading",
  states: {
    loading: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "swipeLoading") {
            router.replace({ name: "swipeLoading" })
          }
        },
      ],
      invoke: {
        id: "loadSwipe",
        src: "loadSwipeData",
        onDone: [
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "new",
            target: "trial",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showQuestions",
            target: "welcomeBack",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showSubmitting",
            target: "submitting",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status ===
              "showLoadResults",
            target: "loadResults",
          },
          {
            guard: ({ event }) =>
              (event.output as { status: string })?.status === "showFinal",
            target: "final",
          },
        ],
      },
    },
    welcomeBack: {
      entry: [() => router.replace({ name: "swipeTest" })],
      on: {
        done: { target: "test" },
      },
    },
    trial: {
      entry: [() => router.replace({ name: "swipeTrial" })],
      on: {
        done: { target: "test" },
      },
    },
    test: {
      entry: [
        () => {
          if (router.currentRoute.value.name !== "swipeTest") {
            router.replace({ name: "swipeTest" })
          }
        },
      ],
      on: {
        done: { target: "submitting" },
      },
    },
    submitting: {
      invoke: {
        src: "autoCompleteExtras",
        onDone: { target: "loadResults" },
        onError: { target: "loadResults" },
      },
    },
    loadResults: {
      entry: [() => router.replace({ name: "swipeLoadResults" })],
      on: {
        done: { target: "final" },
      },
    },
    final: {
      type: "final",
    },
  },
})

export let swipeActor = createActor(swipeMachine)

const swipeActorObserver = {
  async complete() {
    const { isFromInt } = storeToRefs(useUserStore())
    const { getSessionItem } = useSessionStorage()

    const { fetchNextAssessment } = useUserStore()
    const { nextAssessmentName } = storeToRefs(useUserStore())
    const { af_workflow_uuid } = storeToRefs(useHelpStore())

    await fetchNextAssessment(af_workflow_uuid.value ?? null)

    await nextTick()

    if (isFromInt.value && getSessionItem("goto_param")) {
      const didRedirect = await redirectForInt()
      if (didRedirect) return
    }

    if (nextAssessmentName.value) {
      globalActor.send({
        type: `GLOBAL.REDIRECT.${nextAssessmentName.value.toUpperCase()}` as GlobalMachineEvent,
      })
    } else {
      useCookies().remove(COOKIES.WORKFLOW_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.PM_UUID, COOKIES.OPTIONS)
      useCookies().remove(COOKIES.COMPANY_UUID, COOKIES.OPTIONS)
      af_workflow_uuid.value = ""

      const didRedirect = await redirectForInt()
      if (didRedirect) return

      globalActor.send({ type: "GLOBAL.SWIPE.DONE" })
    }
  },
}

swipeActor.subscribe(swipeActorObserver)

async function redirectForInt(): Promise<boolean> {
  const { isFromInt } = storeToRefs(useUserStore())
  const { getSessionItem, deleteSessionItem } = useSessionStorage()

  if (isFromInt.value) {
    if (getSessionItem("goto_param")) {
      deleteSessionItem("goto_param")
    }

    if (getSessionItem("redirect_url")) {
      let redirectUrl = getSessionItem("redirect_url") as string
      deleteSessionItem("redirect_url")
      // Redirect to custom page
      window.location.href = redirectUrl
      return true
    } else {
      // Redirect to thanksATS route
      await router.push({ name: "thanksATS" })
      return true
    }
  }

  return false
}

/**
 * Observateurs extérieurs, réabonnés à chaque acteur neuf.
 *
 * Sans ça, un abonné qui garde la référence de l'acteur — le store d'aide, qui
 * en tire le nom de l'assessment courant, donc les CTA du header — resterait
 * branché sur l'acteur remplacé et deviendrait sourd.
 */
const swipeActorObservers: Array<(_snapshot: unknown) => void> = []

export function subscribeToSwipeActor(observer: (_snapshot: unknown) => void) {
  swipeActorObservers.push(observer)
  swipeActor.subscribe(observer)
}

let hasSwipeActorStarted = false

/**
 * XState ignore `start()` sur un acteur déjà démarré (ou arrivé à son état
 * final) : aucune action d'entrée ne rejoue, la machine ne réapplique donc
 * jamais sa route et l'utilisateur reste sur la route parente nue, écran
 * blanc. Le cas se produit dès qu'on entre deux fois dans l'assessment au
 * cours de la même page — sortie par « non passé », ou renvoi dessus par le
 * workflow. On repart donc d'un acteur neuf.
 *
 * Mais UNIQUEMENT en entrant vraiment dans l'assessment, c'est-à-dire sur la
 * route parente nue, celle par laquelle la machine globale y fait entrer. Le
 * composant racine est aussi remonté sans qu'on change d'assessment — App.vue
 * reconstruit tout `main` quand la langue change — et repartir de zéro là
 * relancerait le test depuis son écran de chargement.
 */
export function startSwipeActor() {
  const isEnteringAssessment = router.currentRoute.value.name === "swipe"

  if (hasSwipeActorStarted && isEnteringAssessment) {
    swipeActor.stop()
    swipeActor = createActor(swipeMachine)
    swipeActor.subscribe(swipeActorObserver)
    for (const observer of swipeActorObservers) swipeActor.subscribe(observer)
  }

  hasSwipeActorStarted = true
  swipeActor.start()
}
